import { useState } from 'react';
import Image from 'next/image';
import { twMerge } from 'tailwind-merge';
import beTwMerge from '@/utils/beTwMerge';
import Title from '@/components/Title/Title';
import { ITitle } from '@/components/BuilderIO/common/CommonFields';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';

export interface StoneCarouselProps {
  /**
   * Optional header.
   * - content*: The content of the header
   * - titleHTMLTag: Heading level that will be used to render the optional header. Defaults to 'h2'
   * - titleSize: Select one of the styles 'extraLarge' | 'large' | 'medium' | 'small'.
   * - titleWeight: Optional header weight. Defaults to 'normal'
   */
  title?: ITitle;
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * Item size: ['large', 'small']. Defaults to 'large'.
   */
  itemSize?: 'large' | 'small';
  /**
   * A list of items. All the properties are required
   * Props:
   * - title: The title of the item
   * - url: The url to open
   * - img: The url of the item image
   * - alt: Alternative text for the image
   */
  items: StoneCarouselItemProps[];
}

export interface StoneCarouselItemProps {
  title: string;
  url: string;
  img: string;
  alt: string;
}

const ITEM_SIZE = {
  large: {
    container: {
      width: '128px',
    },
    imgContainer: {
      height: '128px',
      width: '128px',
    },
    imgSize: {
      height: 128,
      width: 128,
    },
  },
  small: {
    container: {
      gap: '13px',
      padding: '10px 15px',
      paddingBottom: '0',
      width: '100px',
    },
    imgContainer: {
      height: '70px',
      width: '70px',
    },
    imgSize: {
      height: 70,
      width: 70,
    },
  },
};

export default function StoneCarousel({
  title,
  margin = false,
  anchorLinkID,
  itemSize = 'large',
  items = [],
}: StoneCarouselProps) {
  const [oneImageLoaded, setOneImageLoaded] = useState(false);
  const itemLargeStyles =
    title?.content && itemSize === 'large'
      ? 'tw-h-[212px] md:tw-h-[228px] lg:tw-h-[233.5px]'
      : 'tw-h-[155px] md:tw-h-[158px] lg:tw-h-[158px]';

  const itemSmallStyles =
    title?.content && itemSize === 'small'
      ? 'tw-h-[169px] md:tw-h-[185px] lg:tw-h-[190.5px]'
      : 'tw-h-[112px] md:tw-h-[115px] lg:tw-h-[115px]';

  function handleImageLoad() {
    if (!oneImageLoaded) {
      setOneImageLoaded(true);
    }
  }

  return (
    <section
      {...getAnchorId(anchorLinkID)}
      className={beTwMerge(
        'stone-carousel tw-items-center tw-flex tw-flex-col tw-w-full tw-max-w-[1440px] tw-bg-transparent lg:tw-mr-auto lg:tw-ml-auto tw-max-h-[220px] md:tw-max-h-[272px] lg:tw-max-h-[278px]',
        itemSize === 'large' ? itemLargeStyles : itemSmallStyles,
        oneImageLoaded && 'tw-h-auto md:tw-h-auto lg:tw-h-auto',
        getComponentMargin(margin)
      )}
    >
      {title?.content && (
        <Title
          component={title.titleHTMLTag}
          size={title.titleSize}
          weight={title.titleWeight}
          className={twMerge(
            title.titleHTMLTag === 'h2' && 'tw-text-center tw-w-[unset] tw-pl-[15px] tw-mt-0 md:tw-pl-0'
          )}
        >
          {title.content}
        </Title>
      )}
      <div className="content tw-overflow-hidden tw-flex tw-flex-row tw-justify-center tw-w-full xl:tw-mx-[auto] xl:tw-my-[0] xl:tw-w-[88%]">
        <ul className="slideContainer stones-list tw-flex tw-flex-row tw-mt-0 tw-pl-0 tw-mb-0 tw-list-none tw-list-image-none tw-text-center tw-w-full tw-justify-between lg:tw-overflow-x-hidden lg:tw-gap-0">
          {items.map((item: StoneCarouselItemProps, index: number) => (
            <li
              key={index}
              className="stone-carousel__slide tw-px-[8px] tw-py-[0] first-of-type:tw-pl-0 last-of-type:tw-pr-0 lg:tw-p-0"
            >
              <a
                href={item.url}
                style={ITEM_SIZE[itemSize].container}
                className="tw-items-center tw-flex tw-flex-col tw-gap-[8px] hover:tw-no-underline tw-group"
              >
                {/* Image Container */}
                <div className="slideImgContainer tw-flex tw-justify-center" style={ITEM_SIZE[itemSize].imgContainer}>
                  <Image
                    src={item.img}
                    alt={item.alt || item.title || ''}
                    width={ITEM_SIZE[itemSize].imgSize.width}
                    height={ITEM_SIZE[itemSize].imgSize.height}
                    loading="lazy"
                    onLoad={() => handleImageLoad()}
                  />
                </div>
                <p className="tw-font-book tw-text-be-sm desktop:tw-text-be-base tw-text-be-dark-gray tw-m-0 group-hover:tw-text-color-light-green">
                  {item.title}
                </p>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
